import styled from "styled-components";
import InfiniteScroll from "react-infinite-scroll-component";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { useContext, useEffect, useMemo, useState } from "react";
import { convertFromRaw, convertToRaw, DraftInlineStyleType, EditorState, RawDraftContentState } from "draft-js";

import LanguageModal from "./LanguageModal";
import EditorSelect from "./tabs/EditorSelect";
import AIHumansEditorTabs from "./AIHumansEditorTabs";
import Tooltip from "../../../components/Tooltip/Tooltip";
import AIHumansScriptPlayer from "./AIHumansScriptPlayer";
import TextArea from "../../../components/TextArea/TextArea";
import Soundtrack from "../../../components/Sountrack/Soundtrack";
import ActionMenu from "../../../components/ActionMenu/ActionMenu";
import ProgressBar from "../../../components/ProgressBar/ProgressBar";
import Editor, { Block } from "../../../components/Editor/Editor";
import Button, { ButtonThemes } from "../../../components/Button/Button";
import IconButton, { IconButtonThemes } from "../../../components/Button/IconButton";
import {
  getActorsList,
  getCurrentPageDownloadHistory,
  getDownloadHistory,
  getDownloadHistoryLoading,
  getHasMoreDownloadHistory,
} from "../../../redux/reducers/actorReducer";
import { IActor } from "../../../types/actor";
import { cutText } from "../../../lib/textUtils";
import { VideoEditorContext } from "../../../App";
import { AssetModules } from "../../../types/asset";
import { dataToState } from "../../../lib/projectUtils";
import { featuresSettings } from "../../../mocks/humans";
import { editorSelectOptions } from "../../../mocks/actors";
import { setInlineStyle } from "../../../lib/setInlineStyle";
import { getFullImageUrl } from "../../../lib/getFullImageUrl";
import { getAssets } from "../../../redux/reducers/assetReducer";
import { getProfile } from "../../../redux/reducers/profileReducer";
import { Avatar, ObjectTypes, SceneObject } from "../../../types/scene";
import { Popups, updatePopup } from "../../../redux/actions/popupsActions";
import { clearTranslateState } from "../../../redux/actions/languageAction";
import { ConfirmationDelete, DeleteType } from "../../../types/confirmationDelete";
import { CircularProgress, DropdownIconThin } from "../../../components/Icons/Icons";
import { Asset, clearAsset, getAssetServer } from "../../../redux/actions/assetAction";
import { clearDownloadHistory, getDownloadHistoryServer } from "../../../redux/actions/actorActions";
import { getTranslateTextData, getTranslateTextLoading } from "../../../redux/reducers/languageReducer";

export enum EditorTabs {
  textEditor = "textEditor",
  myClips = "myClips",
}

const tabsPanelOptions = [
  {
    value: EditorTabs.textEditor,
    label: "Text script",
  },
  {
    value: EditorTabs.myClips,
    label: "My clips",
  },
];

export interface AIHumansEditorProps {
  setActorActive: (actors: IActor[]) => void;
  setIsPlayScript: (isPlayScript: boolean) => void;
}

const AIHumansEditor = ({ setActorActive, setIsPlayScript }: AIHumansEditorProps) => {
  const {
    featureActive,
    selectedZone,
    paragraphs,
    paragraphActive,
    handleEditorContent,
    editorContent,
    handleTextParagraph,
    handleUpdateSoundtrack,
    setFeatureActive,
    lastSel,
    setLastSel,
    paragraphActor,
    calcParagraphsLength,
    styleMap,
    currentScene,
    scenes,
    activeSceneId: actvSceneId,
    setEditorContent,
    setParagraphs,
    editorSelect,
    setEditorSelect,
  } = useContext(VideoEditorContext);

  const activeSceneId = actvSceneId || 0;
  const newCalcParagraphsLength = calcParagraphsLength(paragraphs);
  const textLength = newCalcParagraphsLength[paragraphActive];

  const [menuOpen, setMenuOpen] = useState<number>();
  const [tabValue, setTabValue] = useState<EditorTabs>(EditorTabs.textEditor);
  const [activeSoundtrack, setActiveSoundtrack] = useState<number>(0);

  const dispatch = useDispatch();
  const actors = useSelector(getActorsList);
  const translateTextData = useSelector(getTranslateTextData);
  const translateTextLoading = useSelector(getTranslateTextLoading);

  const currentObject = currentScene?.objects?.find((obj: SceneObject) => obj.type === ObjectTypes.avatars)?.object as
    | Avatar
    | undefined;

  const assets = useSelector(getAssets);
  const uploadSampleLoading = assets[Asset.Sample].uploadLoading;
  const samples = assets[Asset.Sample].data;
  const samplesLoading = assets[Asset.Sample].isLoading;
  const samplesHasMore = assets[Asset.Sample].hasMore;
  const samplesCurrentPage = assets[Asset.Sample].pageNumber;
  const myClipsData = useSelector(getDownloadHistory);
  const myClipsLoading = useSelector(getDownloadHistoryLoading);
  const myClipsHasMore = useSelector(getHasMoreDownloadHistory);
  const myClipsCurrentPage = useSelector(getCurrentPageDownloadHistory);

  const mergedData = useMemo(() => {
    const updatedMyClipsData = myClipsData.map((item) => ({
      fileName: item.voiceOverText,
      path: item.mp3Path,
      userId: null,
      userAssetID: null,
      ...item,
    }));

    return [...samples, ...updatedMyClipsData].sort(
      (a, b) => new Date(b.insertDateTime).getTime() - new Date(a.insertDateTime).getTime(),
    );
  }, [samples, myClipsData]);

  const profile = useSelector(getProfile);
  const isGuest = profile.roleId === 3;
  const isFreeUser = profile.aiHumanFreeUser;
  const isUploadVoiceEnabled = profile.isUploadVoiceEnabled;
  const AI_HUMANS_LIMITATION = isFreeUser ? 500 : currentObject?.isSadtalker ? 1000 : 2000;

  const activeSceneIdx = scenes.findIndex((scene) => scene.id === activeSceneId);
  const activeScene = scenes[activeSceneIdx];
  const isSimpleEditor = editorSelect[activeSceneIdx] === editorSelectOptions[0].value;

  const newTabsPanelOptions = isGuest
    ? tabsPanelOptions.filter(({ value }) => value !== EditorTabs.myClips)
    : tabsPanelOptions;

  const currentParagraph = paragraphs?.[paragraphActive];

  const fetchMore = () => {
    if (samplesHasMore) {
      dispatch(
        getAssetServer({
          keyword: "",
          pageNumber: samplesCurrentPage + 1,
          assetTypeId: Asset.Sample,
        }),
      );
    }
    if (myClipsHasMore) {
      dispatch(
        getDownloadHistoryServer({
          pageNumber: myClipsCurrentPage + 1,
          pageSize: 16,
        }),
      );
    }
  };

  const handleOpenMenu = (id: number) => setMenuOpen(id);

  const handleCloseMenu = () => setMenuOpen(-1);

  const handleSoundtrackSelected = (path: string) => {
    if (currentScene.audioPath === path) {
      handleUpdateSoundtrack("");
    } else {
      handleUpdateSoundtrack(path);
    }
  };

  const handleTabsChange = (newValue: EditorTabs) => setTabValue(newValue);

  const handleUserAssetDelete = (e: any, id: number | null) => {
    if (!id) {
      throw Error(`The ID ${AssetModules[Asset.Sample]} is missing when deleting`);
    }
    e.stopPropagation();
    e.preventDefault();

    const prefilled: ConfirmationDelete = {
      id,
      title: `Are you sure you want to delete ${AssetModules[Asset.Sample]}?`,
      description: `The ${AssetModules[Asset.Sample]} will be removed and the action cannot be undone`,
      typeId: Asset.Sample,
      type: DeleteType.Asset,
    };

    dispatch(
      updatePopup({
        popup: Popups.confirmationPopup,
        status: true,
        prefilled,
      }),
    );
  };

  const handleChange = (order: number, step: number) => (e: any) => {
    const editorIndex = scenes.findIndex((scene) => scene.id === activeSceneId);
    let content: RawDraftContentState = convertToRaw(editorContent[editorIndex].getCurrentContent());
    const key = content.blocks[featureActive].key;
    const activeFeatureSettings: any = featuresSettings.features[order];
    const pause = content.blocks[featureActive + 1];

    if (activeFeatureSettings.defaultTitle === "pause") {
      const value = activeFeatureSettings.values[Math.round(e.target.value / step)].text + "s";

      if (!pause) {
        content.blocks.push(
          new Block({
            key: Math.random().toString(16).slice(2).slice(0, 5),
            text: value,
            inlineStyles: [{ offset: 0, length: value.length, style: "pause" }],
          }),
          new Block({
            key: Math.random().toString(16).slice(2).slice(0, 5),
            text: "",
            inlinyStyles: [],
          }),
        );
      } else {
        pause.text = value;
        pause.inlineStyleRanges = [{ offset: 0, length: value.length, style: "pause" as DraftInlineStyleType }];
      }
    } else
      content = setInlineStyle(content, key, {
        [activeFeatureSettings.defaultTitle]: activeFeatureSettings.values[Math.round(e.target.value / step)].text,
      });

    const newState = EditorState.forceSelection(EditorState.createWithContent(convertFromRaw(content)), lastSel);

    handleEditorContent(editorIndex)(newState);
  };

  useEffect(() => {
    dispatch(clearAsset({ assetTypeId: Asset.Sample }));
    dispatch(
      getAssetServer({
        keyword: "",
        pageNumber: 1,
        assetTypeId: Asset.Sample,
      }),
    );
    dispatch(clearDownloadHistory());
    dispatch(
      getDownloadHistoryServer({
        pageNumber: 1,
        pageSize: 16,
      }),
    );
  }, []);

  const handlePlayingSoundtrack = (id: number) => setActiveSoundtrack(id - 1);

  useEffect(() => {
    if (translateTextData.length) {
      let translateTextDataIndex = 0;
      const newParagraphs = paragraphs.map((paragraph, index: number) =>
        index === paragraphActive
          ? {
              ...paragraph,
              data: paragraph.data.map((item, order: number) => {
                if (item.text === "") {
                  return item;
                } else {
                  return { ...item, text: translateTextData[translateTextDataIndex++] || item.text };
                }
              }),
            }
          : paragraph,
      );
      setParagraphs(newParagraphs);
      setEditorContent(newParagraphs.map((paragraph) => dataToState(paragraph.data, 0)));
      dispatch(clearTranslateState());
    }
  }, [translateTextData]);

  const uploadAudioHandler = () => {
    dispatch(
      updatePopup({
        popup: Popups.uploadAudioPopup,
        status: true,
        prefilled: {},
      }),
    );
  };

  const handleChooseVoiceActor = () => {
    if (!paragraphActor || !paragraphActor.actorId) return;

    dispatch(
      updatePopup({
        popup: Popups.characterPopup,
        status: true,
        prefilled: {
          actors,
          active: paragraphActor.actorId,
          isSingleActorSelectionOnly: true,
          paragraphs,
          // speechCategory,
          currentParagraphActor: paragraphActor,
          currentParagraphActorsList: paragraphActor ? [paragraphActor] : [],
          setActorActive,
        },
      }),
    );
  };

  const handleEditorSelectChanges = (index: number) => (newEditorSelect: string) => {
    if (index == null) return;

    const showToast = paragraphs[index].data.some((zone) => zone.text);

    if (showToast) {
      dispatch(
        updatePopup({
          popup: Popups.aiVoicesText2SpeechReplaceProjectPopup,
          status: true,
          prefilled: {
            onCancel: () => null,
            onConfirm: () => {
              const newParagraphs = paragraphs.map((paragraph, order) =>
                order === index ? { ...paragraph, data: [{ text: "", features: [] }] } : paragraph,
              );

              setParagraphs(newParagraphs);
              setEditorContent(
                editorContent.map((content, order) => (order === index ? EditorState.createEmpty() : content)),
              );
              setEditorSelect(editorSelect.map((select, order) => (order === index ? newEditorSelect : select)));
            },
          },
        }),
      );
      return;
    }

    setEditorSelect(editorSelect.map((select, order) => (order === index ? newEditorSelect : select)));
  };

  const tabsContent = {
    [EditorTabs.textEditor]: (
      <EditorTabWrapper>
        <DropdownWrapper>
          <VoiceActorDetailsWrapper onClick={handleChooseVoiceActor}>
            <VoiceActorImage src={paragraphActor?.photo} alt="" />
            <VoiceActorName>{paragraphActor?.name}</VoiceActorName>
            {paragraphActor?.flagPath && (
              <Flag>
                <img
                  src={paragraphActor?.flagPath ? getFullImageUrl(paragraphActor.flagPath) : "/images/flag.png"}
                  alt=""
                />
                {paragraphActor?.languageName && <Tooltip text={paragraphActor.languageName} />}
              </Flag>
            )}
          </VoiceActorDetailsWrapper>
          <ActionWrapper>
            {featuresSettings.features
              .filter((feature) => !isSimpleEditor || (feature.id !== 2 && feature.id !== 4)) // TODO: fix
              .map((feature, index) => {
                const activeData = selectedZone;
                const activeFeatureValue = activeData?.features?.find(
                  (features) => features.key === feature.defaultTitle,
                )?.value;

                const barId = feature.values?.find((value) => value.text === activeFeatureValue)?.id;

                const barDefaultId = feature.values?.find((value) => value.text === feature.defaultValue)?.id;
                const barValue = feature.step ? ((barId as number) - 1) * feature.step : 0;
                const barDefaultValue = feature.step ? ((barDefaultId as number) - 1) * feature.step : 0;

                const activeBarLabel = feature.values?.find((value) => value.id === barId)?.label;

                return (
                  <IconButtonWrapper key={feature.id} isTranslate={feature.title === "translate"}>
                    <ActionMenu
                      position={{
                        x: "left",
                        y: "bottom",
                      }}
                      open={index === menuOpen}
                      handleClose={handleCloseMenu}
                      trigger={
                        <FlexWrapper>
                          {feature.title === "Upload audio" ? (
                            <UploadButtonWrapper>
                              <Button
                                buttonTheme={ButtonThemes.Secondary}
                                icon={uploadSampleLoading ? <CircularProgress color="#ffffff" /> : feature.icon}
                                disabled={!isUploadVoiceEnabled}
                                onClick={uploadAudioHandler}
                              />
                              <Tooltip
                                text={feature.title}
                                position="bottom"
                                style={{ boxShadow: "none", borderRadius: "4px", padding: "4px 8px 6px" }}
                                reverseColor
                                arrow
                              />
                            </UploadButtonWrapper>
                          ) : (
                            <ButtonWrapper>
                              <IconButton
                                iconButtonTheme={IconButtonThemes.Rounded}
                                icon={feature.icon}
                                onClick={() => handleOpenMenu(index)}
                              />
                              <Tooltip
                                text={feature.title}
                                position="bottom"
                                style={{ boxShadow: "none", borderRadius: "4px", padding: "4px 8px 6px" }}
                                reverseColor
                                arrow
                              />
                            </ButtonWrapper>
                          )}
                        </FlexWrapper>
                      }
                    >
                      {feature.title !== "upload audio" && (
                        <>
                          {feature.step ? (
                            <ProgressBar
                              {...feature}
                              onChange={handleChange(index, feature.step)}
                              value={!isNaN(barValue) ? barValue : barDefaultValue}
                              activeBarLabel={activeBarLabel || feature.defaultValue?.toString()}
                            />
                          ) : (
                            <LanguageModal paragraph={paragraphs[paragraphActive]?.data} setMenuOpen={setMenuOpen} />
                          )}
                        </>
                      )}
                    </ActionMenu>
                  </IconButtonWrapper>
                );
              })}
          </ActionWrapper>
        </DropdownWrapper>
        {activeScene &&
          paragraphs[activeSceneIdx] &&
          (isSimpleEditor ? (
            <TextAreaWrapper>
              <TextArea
                value={paragraphs[activeSceneIdx].data[0]?.text}
                placeholder="Please enter text"
                onChange={(e) => {
                  if (e.target.value.length > AI_HUMANS_LIMITATION) {
                    toast.error(
                      `Please make sure your project doesn’t exceed the maximum amount of ${AI_HUMANS_LIMITATION} characters, this is done to achieve high quality and rendering speed of the output`,
                    );
                    return;
                  }
                  handleTextParagraph(activeSceneIdx, activeScene.id)(e);
                }}
              />
            </TextAreaWrapper>
          ) : (
            editorContent[activeSceneIdx] && (
              <Editor
                key={activeScene.id}
                index={activeSceneIdx}
                editorContent={editorContent[activeSceneIdx]}
                setEditorContent={handleEditorContent(activeSceneIdx)}
                handleTextParagraph={handleTextParagraph(activeSceneIdx, activeScene.id)}
                setFeatureActive={setFeatureActive}
                lastSel={lastSel}
                setLastSel={setLastSel}
                paragraphs={paragraphs}
                styleMap={styleMap}
                textLength={textLength}
                limitation={AI_HUMANS_LIMITATION}
                defaultPause={0}
                placeholder={"Please enter text"}
              />
            )
          ))}
        <Footer>
          <span>
            {textLength}/{AI_HUMANS_LIMITATION}
          </span>
        </Footer>
        {editorSelect[activeSceneIdx] && (
          <EditorSelect
            optionsList={editorSelectOptions}
            value={editorSelect[activeSceneIdx]}
            onChange={handleEditorSelectChanges(activeSceneIdx)}
            actionIcon={<DropdownIconThin />}
          />
        )}
        {currentParagraph && (
          <AIHumansScriptPlayer currentParagraph={currentParagraph} setIsPlayScript={setIsPlayScript} />
        )}
      </EditorTabWrapper>
    ),
    [EditorTabs.myClips]: (
      <SoundtrackWrapper id="scrollableSampleDiv">
        {(samplesLoading || myClipsLoading) && mergedData.length === 0 ? (
          <CircularProgressWrapper>
            <CircularProgress color="#009af7" />
          </CircularProgressWrapper>
        ) : (
          <InfiniteScroll
            next={fetchMore}
            hasMore={samplesHasMore || myClipsHasMore}
            loader={
              samplesLoading || myClipsLoading ? (
                <CircularProgressWrapper>
                  <CircularProgress color="#009af7" />
                </CircularProgressWrapper>
              ) : null
            }
            dataLength={mergedData?.length}
            style={{ display: "contents" }}
            scrollableTarget="scrollableSampleDiv"
          >
            {mergedData.map((data, index: number) => {
              const selectedSoundtrack = data.path === currentScene.audioPath;

              return (
                <Soundtrack
                  key={index}
                  active={index === activeSoundtrack + 1}
                  title={cutText(data.fileName, 40)}
                  deleteActive={!!data.userId}
                  soundtrack={data.path}
                  selectedSoundtrack={selectedSoundtrack}
                  variant="editor"
                  handleSelect={() => handleSoundtrackSelected(data.path)}
                  onClick={() => handlePlayingSoundtrack(index)}
                  handleDelete={(e: any) => handleUserAssetDelete(e, data.userAssetID)}
                  category={data.userAssetID ? "U" : "G"}
                />
              );
            })}
          </InfiniteScroll>
        )}
      </SoundtrackWrapper>
    ),
  };

  useEffect(() => {
    const activeScene = scenes.find((scene) => scene.id === activeSceneId);
    const voice = activeScene?.audioPath;
    if (activeScene && voice) {
      // setTabValue(EditorTabs.uploadVoice);
    } else {
      setTabValue(EditorTabs.textEditor);
    }
  }, [activeSceneId]);

  return (
    <Wrapper isLoading={translateTextLoading}>
      {translateTextLoading ? (
        <LoadingText>Please wait, the text is being translated</LoadingText>
      ) : (
        <>
          <AIHumansEditorTabs active={tabValue} data={newTabsPanelOptions} handleTabsChange={handleTabsChange} />
          <Content>{tabValue && tabsContent[tabValue]}</Content>
        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ isLoading: boolean }>`
  width: 100%;
  position: relative;

  ${({ isLoading }) =>
    isLoading &&
    `
      display: flex;
      align-items: center;
      justify-content: center;
    `}
`;
const EditorTabWrapper = styled.div`
  position: relative;
  min-height: 480px;
  /* max-height: 480px; */

  .RichEditorContainer {
    padding: 16px;
    margin-top: 0;
    /* max-height: 380px; */
    min-height: 380px;
    height: calc(100vh - 290px);
    overflow: hidden auto;
    padding-right: 20px;
    background: ${({ theme }) => theme.black1};
    border-width: 0px 1px 1px 1px;
    border-style: solid;
    border-color: ${({ theme }) => theme.tableDivider}70;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;

    ::-webkit-scrollbar {
      width: 2px;
    }

    ::-webkit-scrollbar-thumb {
      border-radius: 10px;
      background-color: ${({ theme }) => theme.activeMenu};
    }

    ::-webkit-scrollbar-track {
      margin: 15px;
    }

    .public-DraftEditorPlaceholder-inner {
      color: #ffffff;
    }
  }
`;

const Content = styled.div`
  /* border-top: 2px solid ${({ theme }) => theme.editorLineBorder};
  margin-top: -2px; */
`;

const DropdownWrapper = styled.div`
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.primaryBackground1};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top-right-radius: 20px;

  .dropdown-menu {
    min-width: 15rem;
  }

  .dropdown > button > button {
    display: none;
  }
`;

const VoiceActorDetailsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 25px;
  cursor: pointer;
`;
const VoiceActorImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
`;
const VoiceActorName = styled.span`
  color: ${({ theme }) => theme.primaryText};
  font-family: "Mulish", sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
`;
const Flag = styled.div`
  position: relative;
  min-width: 26px;
  width: 26px;
  height: auto;
  display: flex;
  align-items: center;

  img {
    width: 100%;
    height: auto;
    transform: translateY(1px);
  }

  img + div {
    width: 200px;
    top: 15px;

    span {
      font-size: 12px !important;
    }
  }

  :hover > div {
    opacity: 1;
    visibility: visible;
  }
`;

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  button {
    background: #151515;
    box-shadow: none;
    border-radius: 8px;
    max-width: 32px;
    min-width: 32px;
    height: 32px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const IconButtonWrapper = styled.div<{ isTranslate: boolean }>`
  position: relative;
  width: 32px;
  height: 32px;

  .menu-wrapper {
    top: 38px;
    right: -4px;
    padding: 16px;
    min-width: 240px;
    background: ${({ theme }) => theme.black1};
    box-shadow: none;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border: none;

    .progress-bar-title {
      font-size: 16px !important;
      text-transform: capitalize;
    }
  }

  ${({ isTranslate }) =>
    isTranslate &&
    `
    .menu-wrapper {
      top: 40px;
      left: -20px;
      min-height: 296px;
      max-height: 296px;
      overflow: hidden;
    }
  `}
`;

const Footer = styled.div`
  padding: 0 16px 24px 0;
  display: flex;
  width: 90px;
  justify-content: end;
  margin-top: auto;
  position: absolute;
  z-index: 5;
  right: 0;
  bottom: 40px;
  text-align: center;

  span {
    font-family: "Mulish", sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    color: ${({ theme }) => theme.primaryText}66;
    background-color: ${({ theme }) => theme.editor};
  }
`;

const SoundtrackWrapper = styled.div`
  padding-top: 24px;
  position: relative;
  overflow: hidden auto;
  height: calc(100vh - 100px);
  min-height: 436px;
  /* max-height: 436px; */
  display: flex;
  flex-direction: column;
  /* gap: 12px; */
  align-content: start;
  background-color: ${({ theme }) => theme.secondaryBackground1};
  border-top-right-radius: 20px;
  gap: 12px;
  padding-bottom: 20px;

  .infinite-scroll-component__outerdiv {
    display: contents;
  }

  .soundtrack-delete {
    width: auto;
    height: auto;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  @media (max-width: 1001px) {
    ::-webkit-scrollbar {
      display: none;
    }
  }

  ::-webkit-scrollbar {
    /* width: 2px; */
    display: none;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: ${({ theme }) => theme.activeMenu};
  }

  ::-webkit-scrollbar-track {
    margin: 15px 0;
  }
`;

const CircularProgressWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;
const FlexWrapper = styled.div`
  display: flex;
  gap: 8px;
`;
const ButtonWrapper = styled.div`
  position: relative;

  & > div {
    width: max-content;
  }

  &:hover > div {
    opacity: 1;
    visibility: visible;
  }
`;
const UploadButtonWrapper = styled(ButtonWrapper)`
  button {
    background-color: ${({ theme }) => theme.button};
  }

  input {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }

  input[type="file"],
  input[type="file"]::-webkit-file-upload-button {
    cursor: pointer;
  }
`;

const LoadingText = styled.div`
  color: #ffffff;
  line-height: 16px;
  font-weight: 500;
  text-align: center;
`;

const TextAreaWrapper = styled.div`
  display: flex;
  width: 100%;
  height: calc(100vh - 290px);

  padding: 16px 0 32px 16px;
  background: ${({ theme }) => theme.black1};
  border-width: 0px 1px 1px 1px;
  border-style: solid;
  border-color: ${({ theme }) => theme.tableDivider}70;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;

  textarea {
    margin-top: 0;
    height: 100%;

    ::-webkit-scrollbar {
      width: 2px;
    }

    ::-webkit-scrollbar-thumb {
      border-radius: 10px;
      background-color: ${({ theme }) => theme.activeMenu};
    }

    ::-webkit-scrollbar-track {
      margin: -15px;
    }
  }
`;

export default AIHumansEditor;
